import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getCulture, getProducts } from '../api/api';
import './CultureDetail.css';

const CultureDetail = () => {
  const { cultureName } = useParams();
  const navigate = useNavigate();
  const [culture, setCulture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [cultureProducts, setCultureProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    fetchCultureDetail();
    // eslint-disable-next-line
  }, [cultureName]);

  const fetchCultureDetail = async () => {
    try {
      const response = await getCulture(cultureName);
      // Support multiple API shapes: unwrap nested `data` layers until we reach the culture object
      let cultureObj = response && response.data ? response.data : response;
      // Unwrap repeated `.data` layers (some backends return { data: { data: { ... } } })
      while (cultureObj && cultureObj.data && (typeof cultureObj.data === 'object') && !cultureObj.overview && !cultureObj.description) {
        cultureObj = cultureObj.data;
      }
      cultureObj = cultureObj || {};
      setCulture(cultureObj);
      setLoading(false);
      fetchCultureProducts();
    } catch (err) {
      setError('Failed to load culture information');
      setLoading(false);
    }
  };

  const fetchCultureProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await getProducts({ culture: cultureName, limit: 8 });
      const products = response.data.products || response.data || [];
      setCultureProducts(products.filter(p => p.isPublished !== false).slice(0, 8));
    } catch (err) {
      // silently fail — products section just stays empty
    } finally {
      setProductsLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return 'https://picsum.photos/seed/placeholder/500/500';
    const url = typeof image === 'string' ? image : image.url;
    if (!url) return 'https://picsum.photos/seed/placeholder/500/500';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_URL || ''}/uploads/${url}`;
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!culture) return <div className="error">Culture not found</div>;

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'history', label: 'History' },
    { id: 'language', label: 'Language' },
    { id: 'traditions', label: 'Traditions' },
    { id: 'food', label: 'Food' },
    { id: 'practices', label: 'Cultural Practices' },
    { id: 'social', label: 'Social Structure' },
    { id: 'modern', label: 'Modern Presence' },
    { id: 'references', label: 'References' }
  ];

  return (
    <div className="culture-detail">
      <div className="culture-hero">
        <Link to="/cultures" className="back-link">← Back to All Cultures</Link>
        <h1>{culture.cultureName || culture.displayName}</h1>
        {culture.alternativeNames && culture.alternativeNames.length > 0 && (
          <p className="alternative-names">
            Also known as: {culture.alternativeNames.join(', ')}
          </p>
        )}
      </div>

      <div className="culture-nav">
        {sections.map((section) => (
          <button
            key={section.id}
            className={`nav-btn ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="culture-content">
        {activeSection === 'overview' && (
          <section className="content-section">
            <h2>Overview</h2>
            {culture.overview || culture.description ? (
              <p>{culture.overview || culture.description}</p>
            ) : (
              <p className="no-content">Detailed information about {culture.cultureName} culture is being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'history' && (
          <section className="content-section">
            <h2>History</h2>
            
            {culture.history && culture.history.overview && (
              <div className="subsection">
                <p>{culture.history.overview}</p>
              </div>
            )}
            
            {culture.history && culture.history.firstSettlement && (
              <div className="subsection">
                <h3>First Settlement</h3>
                <p><strong>Period:</strong> {culture.history.firstSettlement.period}</p>
                <p><strong>Location:</strong> {culture.history.firstSettlement.location}</p>
                <p>{culture.history.firstSettlement.description}</p>
                {culture.history.firstSettlement.sources && culture.history.firstSettlement.sources.length > 0 && (
                  <div className="sources">
                    <strong>Sources:</strong>
                    <ul>
                      {culture.history.firstSettlement.sources.map((source, idx) => (
                        <li key={idx}>{source}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {culture.history && (culture.history.migrations || culture.history.migration) && (
              <div className="subsection">
                <h3>Migration Patterns</h3>
                <p>{culture.history.migrations || culture.history.migration}</p>
              </div>
            )}

            {culture.history && (culture.history.keyEvents || culture.history.keyHistoricalEvents) && (
              <div className="subsection">
                <h3>Key Historical Events</h3>
                {Array.isArray(culture.history.keyEvents) ? (
                  culture.history.keyEvents.map((event, idx) => (
                    <div key={idx} className="historical-event">
                      <h4>{event.event} ({event.period})</h4>
                      <p>{event.description}</p>
                      {event.sources && event.sources.length > 0 && (
                        <div className="sources">
                          <strong>Sources:</strong>
                          <ul>
                            {event.sources.map((source, sidx) => (
                              <li key={sidx}>{source}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                ) : Array.isArray(culture.history.keyHistoricalEvents) ? (
                  culture.history.keyHistoricalEvents.map((event, idx) => (
                    <div key={idx} className="historical-event">
                      <h4>{event.event} ({event.period})</h4>
                      <p>{event.description}</p>
                      {event.sources && event.sources.length > 0 && (
                        <div className="sources">
                          <strong>Sources:</strong>
                          <ul>
                            {event.sources.map((source, sidx) => (
                              <li key={sidx}>{source}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="no-content">Historical events information is being compiled. Check back soon!</p>
                )}
              </div>
            )}
            
            {!culture.history || (!culture.history.overview && !culture.history.firstSettlement && !culture.history.migrations && !culture.history.migration && !culture.history.keyEvents && !culture.history.keyHistoricalEvents) && (
              <p className="no-content">Detailed historical information about {culture.cultureName} is being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'language' && (
          <section className="content-section">
            <h2>Language</h2>
            {culture.language && typeof culture.language === 'object' ? (
              <>
                {culture.language.primary && <p><strong>Primary Language:</strong> {culture.language.primary}</p>}
                {culture.language.name && <p><strong>Name:</strong> {culture.language.name}</p>}
                {culture.language.family && <p><strong>Language Family:</strong> {culture.language.family}</p>}
                {culture.language.speakers && <p><strong>Speakers:</strong> {typeof culture.language.speakers === 'number' ? culture.language.speakers.toLocaleString() : culture.language.speakers}</p>}
              </>
            ) : (
              <p className="no-content">Language information about {culture.cultureName} is being compiled. Check back soon!</p>
            )}
            
            {culture.language && typeof culture.language === 'object' && culture.language.uniqueFeatures && (
              <div className="subsection">
                <h3>Unique Features</h3>
                {Array.isArray(culture.language.uniqueFeatures) ? (
                  <ul>
                    {culture.language.uniqueFeatures.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{culture.language.uniqueFeatures}</p>
                )}
              </div>
            )}

            {culture.language && typeof culture.language === 'object' && Array.isArray(culture.language.commonPhrases) && culture.language.commonPhrases.length > 0 && (
              <div className="subsection">
                <h3>Common Phrases</h3>
                <div className="phrases-list">
                  {culture.language.commonPhrases.map((phrase, idx) => (
                    <div key={idx} className="phrase-card">
                      <p className="phrase">{phrase.phrase}</p>
                      <p className="translation">{phrase.meaning || phrase.translation}</p>
                      {phrase.pronunciation && <p className="pronunciation">({phrase.pronunciation})</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {culture.language && typeof culture.language === 'object' && Array.isArray(culture.language.sources) && culture.language.sources.length > 0 && (
              <div className="sources">
                <strong>Sources:</strong>
                <ul>
                  {culture.language.sources.map((source, idx) => (
                    <li key={idx}>{source}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {activeSection === 'traditions' && (
          <section className="content-section">
            <h2>Traditions</h2>

            {culture.traditions && typeof culture.traditions === 'object' && !Array.isArray(culture.traditions) ? (
              <>
                {culture.traditions.ceremonies && typeof culture.traditions.ceremonies === 'string' && (
                  <div className="subsection">
                    <h3>Ceremonies</h3>
                    <p>{culture.traditions.ceremonies}</p>
                  </div>
                )}

                {culture.traditions.music && typeof culture.traditions.music === 'string' && (
                  <div className="subsection">
                    <h3>Music</h3>
                    <p>{culture.traditions.music}</p>
                  </div>
                )}

                {culture.traditions.dance && typeof culture.traditions.dance === 'string' && (
                  <div className="subsection">
                    <h3>Dance</h3>
                    <p>{culture.traditions.dance}</p>
                  </div>
                )}

                {culture.traditions.storytelling && typeof culture.traditions.storytelling === 'string' && (
                  <div className="subsection">
                    <h3>Storytelling</h3>
                    <p>{culture.traditions.storytelling}</p>
                  </div>
                )}
              </>
            ) : culture.traditions && Array.isArray(culture.traditions) && culture.traditions.length > 0 ? (
              <div className="subsection">
                <h3>Cultural Traditions</h3>
                {culture.traditions.map((tradition, idx) => (
                  <div key={idx} className="ceremony-card">
                    <h4>{tradition.name}</h4>
                    <p>{tradition.description}</p>
                    {tradition.significance && <p><strong>Significance:</strong> {tradition.significance}</p>}
                    {tradition.practiced && <p><strong>When Practiced:</strong> {tradition.practiced}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">Detailed traditions and ceremonies of {culture.cultureName} culture are being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'food' && (
          <section className="content-section">
            <h2>Food & Cuisine</h2>
            
            {culture.food ? (
              <>
                {culture.food.traditionDishes && typeof culture.food.traditionDishes === 'string' && (
                  <div className="subsection">
                    <h3>Traditional Dishes</h3>
                    <p>{culture.food.traditionDishes}</p>
                  </div>
                )}
                
                {culture.food.cookingMethods && typeof culture.food.cookingMethods === 'string' && (
                  <div className="subsection">
                    <h3>Cooking Methods</h3>
                    <p>{culture.food.cookingMethods}</p>
                  </div>
                )}
                
                {culture.food.specialOccasions && typeof culture.food.specialOccasions === 'string' && (
                  <div className="subsection">
                    <h3>Special Occasions</h3>
                    <p>{culture.food.specialOccasions}</p>
                  </div>
                )}
              </>
            ) : culture.culturalPractices && culture.culturalPractices.foodAndCuisine && culture.culturalPractices.foodAndCuisine.length > 0 ? (
              <div className="food-grid">
                {culture.culturalPractices.foodAndCuisine.map((food, idx) => (
                  <div key={idx} className="food-item">
                    {food.imageUrl && (
                      <div className="food-image-container">
                        <img 
                          src={food.imageUrl} 
                          alt={food.name} 
                          className="food-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="food-content">
                      <h3>{food.name}</h3>
                      <p className="food-description">{food.description}</p>
                      
                      {food.significance && (
                        <div className="food-significance">
                          <h4>Cultural Significance</h4>
                          <p>{food.significance}</p>
                        </div>
                      )}
                      
                      {food.recipe && (
                        <div className="recipe-section">
                          <h4>Recipe</h4>
                          <div className="recipe-meta">
                            {food.recipe.prepTime && (
                              <p><strong>Prep Time:</strong> {food.recipe.prepTime}</p>
                            )}
                            {food.recipe.servings && (
                              <p><strong>Servings:</strong> {food.recipe.servings}</p>
                            )}
                          </div>
                          
                          {food.recipe.ingredients && food.recipe.ingredients.length > 0 && (
                            <div className="recipe-ingredients">
                              <h5>Ingredients</h5>
                              <ul>
                                {food.recipe.ingredients.map((ing, iidx) => (
                                  <li key={iidx}>{ing}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {food.recipe.instructions && food.recipe.instructions.length > 0 && (
                            <div className="recipe-instructions">
                              <h5>Instructions</h5>
                              <ol>
                                {food.recipe.instructions.map((inst, instidx) => (
                                  <li key={instidx}>{inst}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">Traditional food and cuisine information about {culture.cultureName} is being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'practices' && (
          <section className="content-section">
            <h2>Cultural Practices</h2>

            {culture.practices ? (
              <>
                {culture.practices.culturalPractices && typeof culture.practices.culturalPractices === 'string' && (
                  <div className="subsection">
                    <h3>Cultural Practices</h3>
                    <p>{culture.practices.culturalPractices}</p>
                  </div>
                )}

                {culture.practices.spiritualBeliefs && typeof culture.practices.spiritualBeliefs === 'string' && (
                  <div className="subsection">
                    <h3>Spiritual Beliefs</h3>
                    <p>{culture.practices.spiritualBeliefs}</p>
                  </div>
                )}

                {culture.practices.artForms && typeof culture.practices.artForms === 'string' && (
                  <div className="subsection">
                    <h3>Art Forms</h3>
                    <p>{culture.practices.artForms}</p>
                  </div>
                )}
              </>
            ) : culture.culturalPractices ? (
              <>
                {culture.culturalPractices.clothingAndAdornment && (
                  <div className="subsection">
                    <h3>Clothing & Adornment</h3>
                    <p>{culture.culturalPractices.clothingAndAdornment.description}</p>
                    {culture.culturalPractices.clothingAndAdornment.significance && (
                      <p><strong>Significance:</strong> {culture.culturalPractices.clothingAndAdornment.significance}</p>
                    )}
                    {culture.culturalPractices.clothingAndAdornment.sources && 
                     culture.culturalPractices.clothingAndAdornment.sources.length > 0 && (
                      <div className="sources">
                        <strong>Sources:</strong>
                        <ul>
                          {culture.culturalPractices.clothingAndAdornment.sources.map((source, idx) => (
                            <li key={idx}>{source}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {culture.culturalPractices.artAndCrafts && culture.culturalPractices.artAndCrafts.length > 0 && (
                  <div className="subsection">
                    <h3>Art & Crafts</h3>
                    {culture.culturalPractices.artAndCrafts.map((art, idx) => (
                      <div key={idx} className="art-card">
                        <h4>{art.type}</h4>
                        <p>{art.description}</p>
                        {art.materials && art.materials.length > 0 && (
                          <p><strong>Materials:</strong> {art.materials.join(', ')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {culture.culturalPractices.spiritualBeliefs && (
                  <div className="subsection">
                    <h3>Spiritual Beliefs</h3>
                    <p>{culture.culturalPractices.spiritualBeliefs.description}</p>
                    {culture.culturalPractices.spiritualBeliefs.practices && 
                     culture.culturalPractices.spiritualBeliefs.practices.length > 0 && (
                      <ul>
                        {culture.culturalPractices.spiritualBeliefs.practices.map((practice, idx) => (
                          <li key={idx}>{practice}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="no-content">Detailed cultural practices of {culture.cultureName} are being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'social' && (
          <section className="content-section">
            <h2>Social Structure</h2>
            {culture.socialStructure ? (
              <>
                {culture.socialStructure.familySystems && typeof culture.socialStructure.familySystems === 'string' && (
                  <div className="subsection">
                    <h3>Family Systems</h3>
                    <p>{culture.socialStructure.familySystems}</p>
                  </div>
                )}
                
                {culture.socialStructure.communityOrganization && typeof culture.socialStructure.communityOrganization === 'string' && (
                  <div className="subsection">
                    <h3>Community Organization</h3>
                    <p>{culture.socialStructure.communityOrganization}</p>
                  </div>
                )}
                
                {culture.socialStructure.rolesByAge && typeof culture.socialStructure.rolesByAge === 'string' && (
                  <div className="subsection">
                    <h3>Roles by Age</h3>
                    <p>{culture.socialStructure.rolesByAge}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="no-content">Social structure information about {culture.cultureName} is being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'modern' && (
          <section className="content-section">
            <h2>Modern Presence</h2>
            {culture.modernPresence ? (
              <>
                {culture.modernPresence.population && typeof culture.modernPresence.population === 'string' && (
                  <div className="subsection">
                    <h3>Population</h3>
                    <p>{culture.modernPresence.population}</p>
                  </div>
                )}
                
                {culture.modernPresence.regions && typeof culture.modernPresence.regions === 'string' && (
                  <div className="subsection">
                    <h3>Regions</h3>
                    <p>{culture.modernPresence.regions}</p>
                  </div>
                )}
                
                {culture.modernPresence.efforts && typeof culture.modernPresence.efforts === 'string' && (
                  <div className="subsection">
                    <h3>Cultural Preservation Efforts</h3>
                    <p>{culture.modernPresence.efforts}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="no-content">Modern presence information about {culture.cultureName} is being compiled. Check back soon!</p>
            )}
          </section>
        )}

        {activeSection === 'references' && (
          <section className="content-section">
            <h2>References & Sources</h2>
            {culture.references && culture.references.length > 0 ? (
              <div className="references-list">
                {culture.references.map((ref, idx) => {
                  // Handle both string references and object references
                  if (typeof ref === 'string') {
                    return (
                      <div key={idx} className="reference-card">
                        <p className="harvard-citation">{ref}</p>
                      </div>
                    );
                  } else {
                    // Handle object references in Harvard format
                    let citation = '';
                    if (ref.author) citation += ref.author + ' ';
                    if (ref.year) citation += '(' + ref.year + ') ';
                    if (ref.title) citation += "'" + ref.title + "'. ";
                    if (ref.publication) citation += ref.publication + '. ';
                    
                    return (
                      <div key={idx} className="reference-card">
                        <p className="harvard-citation">{citation}</p>
                        {ref.url && (
                          <a href={ref.url} target="_blank" rel="noopener noreferrer" className="reference-link">
                            View Source →
                          </a>
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            ) : (
              <p className="no-content">Academic references for {culture.cultureName} culture are being compiled and verified. Check back soon!</p>
            )}
          </section>
        )}
      </div>

      {culture.notableFigures && culture.notableFigures.length > 0 && (
        <div className="notable-figures">
          <h2>Notable Figures</h2>
          <div className="figures-grid">
            {culture.notableFigures.map((figure, idx) => (
              <div key={idx} className="figure-card">
                <h3>{figure.name}</h3>
                <p>{figure.contribution}</p>
                {figure.period && <p className="period">{figure.period}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="related-products">
        <div className="related-products-header">
          <h2>Explore {culture.displayName} Products</h2>
          <Link to={`/products?culture=${culture.cultureName}`} className="explore-btn">
            View All Products →
          </Link>
        </div>

        {productsLoading && (
          <p className="products-loading">Loading products…</p>
        )}

        {!productsLoading && cultureProducts.length > 0 && (
          <div className="culture-products-grid">
            {cultureProducts.map(product => (
              <div
                key={product._id}
                className="culture-product-card"
                onClick={() => navigate(`/products/${product._id}`)}
              >
                <div className="culture-product-img-wrap">
                  <img
                    src={getImageUrl(product.images && product.images[0])}
                    alt={product.name}
                    onError={e => { e.target.src = 'https://picsum.photos/seed/placeholder/500/500'; }}
                  />
                </div>
                <div className="culture-product-info">
                  <h4>{product.name}</h4>
                  <p className="culture-product-category">{product.category}</p>
                  <p className="culture-product-price">R{(product.price || 0).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {!productsLoading && cultureProducts.length === 0 && (
          <p className="no-products-yet">
            Products for this culture are coming soon. Check back later!
          </p>
        )}
      </div>
    </div>
  );
};

export default CultureDetail;
